import axios from 'axios'

// Configuración base de Axios dinámica para desarrollo local y acceso LAN
export const getBaseUrl = () => {
  const envUrl = import.meta.env.VITE_API_BASE_URL
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return envUrl || `${window.location.origin}/api`
  }
  if (envUrl && envUrl.startsWith('https://')) {
    return envUrl
  }
  if (typeof window !== 'undefined' && window.location.hostname) {
    const host = window.location.hostname
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:8000/api'
    }
    return `http://${host}:8000/api`
  }
  return envUrl || 'http://localhost:8000/api'
}

export const API_BASE_URL = getBaseUrl()

// Crear instancia de axios
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false
})

// Interceptor para agregar el token de autorización y la empresa activa
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Enviar siempre la empresa activa para que el middleware de plan
    // pueda identificar correctamente qué empresa/plan aplica al request
    const companyId = localStorage.getItem('current_company_id')
    if (companyId) {
      config.headers['X-Company-Id'] = companyId
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor para manejar respuestas y errores
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // Si el token es inválido o expiró, redirigir al login
    // PERO NO si estamos intentando hacer login (para evitar recargas infinitas)
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname
      const isLoginAttempt = error.config?.url?.includes('/auth/login')

      if (!isLoginAttempt && currentPath !== '/login') {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user_data')
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// Servicios de autenticación
export const authService = {
  // Iniciar sesión
  async login(credentials) {
    try {
      const response = await apiClient.post('/auth/login', credentials)

      if (response.data.success) {
        // Guardar token y datos del usuario
        localStorage.setItem('auth_token', response.data.data.access_token)
        localStorage.setItem('user_data', JSON.stringify(response.data.data.user))

        return {
          success: true,
          data: response.data.data,
          message: response.data.message
        }
      }

      return {
        success: false,
        message: response.data.message || 'Error en el inicio de sesión'
      }
    } catch (error) {
      console.error('❌ API: Error en login:', error)


      // Para errores 401 (credenciales incorrectas), usar el mensaje del backend
      if (error.response?.status === 401 && error.response?.data) {

        return {
          success: false,
          message: error.response.data.message || 'Credenciales incorrectas',
          error_type: error.response.data.error_type || 'auth_failed',
          suggestion: error.response.data.suggestion,
          errors: error.response.data.errors || {}
        }
      }

      // Para errores 403 (cuenta no verificada, pendiente de aprobación, suspendida)
      if (error.response?.status === 403 && error.response?.data) {

        return {
          success: false,
          message: error.response.data.message || 'Acceso denegado',
          error_type: error.response.data.error_type || 'access_denied',
          account_status: error.response.data.account_status,
          can_resend_email: error.response.data.can_resend_email,
          errors: error.response.data.errors || {}
        }
      }

      // Para otros errores del servidor
      if (error.response?.data) {



        return {
          success: false,
          message: error.response.data.message || 'Error en el servidor',
          errors: error.response.data.errors || {}
        }
      }

      // Para errores de red o conexión
      return {
        success: false,
        message: 'Error de conexión con el servidor'
      }
    }
  },

  // Registrar usuario
  // Nota: El nuevo flujo NO devuelve token inmediatamente
  // El usuario debe verificar su email primero
  async register(userData) {
    try {
      const response = await apiClient.post('/auth/register', userData)

      if (response.data.success) {
        // El nuevo flujo NO devuelve token
        // Solo devolvemos la respuesta para mostrar mensaje de verificación
        return {
          success: true,
          data: response.data.data,
          message: response.data.message
        }
      }

      return {
        success: false,
        message: response.data.message || 'Error en el registro'
      }
    } catch (error) {
      console.error('Register error:', error)

      if (error.response?.data) {
        return {
          success: false,
          message: error.response.data.message || 'Error en el servidor',
          errors: error.response.data.errors || {}
        }
      }

      return {
        success: false,
        message: 'Error de conexión con el servidor'
      }
    }
  },

  // Verificar email con token
  async verifyEmail(token) {
    try {
      const response = await apiClient.post('/auth/verify-email', { token })
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Verify email error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al verificar email',
        error_type: error.response?.data?.error_type
      }
    }
  },

  // Reenviar email de verificación
  async resendVerificationEmail(email) {
    try {
      const response = await apiClient.post('/auth/resend-verification', { email })
      return {
        success: response.data.success,
        message: response.data.message
      }
    } catch (error) {
      console.error('Resend verification error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al reenviar verificación'
      }
    }
  },

  // Verificar estado de la cuenta
  async checkAccountStatus(email) {
    try {
      const response = await apiClient.post('/auth/check-account-status', { email })
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Check account status error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al verificar estado'
      }
    }
  },

  // Solicitar recuperación de contraseña
  async forgotPassword(email) {
    try {
      const response = await apiClient.post('/auth/forgot-password', { email })
      return {
        success: response.data.success,
        message: response.data.message
      }
    } catch (error) {
      console.error('Forgot password error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al solicitar recuperación de contraseña'
      }
    }
  },

  // Restablecer contraseña
  async resetPassword(data) {
    try {
      const response = await apiClient.post('/auth/reset-password', data)
      return {
        success: response.data.success,
        message: response.data.message
      }
    } catch (error) {
      console.error('Reset password error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al restablecer la contraseña',
        errors: error.response?.data?.errors || {}
      }
    }
  },

  // Cerrar sesión
  async logout() {
    try {
      // Intentar hacer logout en el servidor (ruta correcta)
      await apiClient.post('/user/logout')

    } catch (error) {
      console.error('❌ API: Error en logout del servidor:', error)
      // Continuar con la limpieza local aunque falle el servidor
    } finally {
      // Limpiar datos locales siempre
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_data')

    }
  },

  // Obtener usuario actual
  getCurrentUser() {
    const userData = localStorage.getItem('user_data')
    return userData ? JSON.parse(userData) : null
  },

  // Verificar si está autenticado
  isAuthenticated() {
    const token = localStorage.getItem('auth_token')
    const user = this.getCurrentUser()
    return !!(token && user)
  },

  // Obtener perfil del usuario
  async getProfile() {
    try {
      const response = await apiClient.get('/auth/profile')

      if (response.data.success) {
        // Actualizar datos del usuario
        localStorage.setItem('user_data', JSON.stringify(response.data.data))
        return {
          success: true,
          data: response.data.data
        }
      }

      return {
        success: false,
        message: response.data.message || 'Error al obtener el perfil'
      }
    } catch (error) {
      console.error('Get profile error:', error)
      return {
        success: false,
        message: 'Error de conexión con el servidor'
      }
    }
  }
}

// Servicios para documentos
export const documentService = {
  // Obtener documentos del usuario
  async getDocuments(params = {}) {
    try {
      const response = await apiClient.get('/documents', { params })
      return {
        success: true,
        data: response.data.data || response.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Get documents error:', error)
      return {
        success: false,
        message: 'Error al obtener documentos'
      }
    }
  },

  // Subir documento individual
  async uploadDocument(formData) {
    try {
      const response = await apiClient.post('/documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Upload document error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al subir documento'
      }
    }
  },

  // Carga masiva de XMLs
  async bulkUpload(formData) {
    try {
      const response = await apiClient.post('/documents/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('Bulk upload error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error en carga masiva'
      }
    }
  },

  // Analizar con IA
  async analyzeWithAI(documentId) {
    try {
      const response = await apiClient.post(`/documents/${documentId}/analyze-ai`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error en análisis IA'
      }
    }
  },

  // Generar PDF
  async generatePdf(documentId) {
    try {
      const response = await apiClient.post(`/documents/${documentId}/generate-pdf`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al generar PDF'
      }
    }
  }
}

// Servicios para empresas
export const companyService = {
  // Obtener empresas del usuario
  async getCompanies() {
    try {
      const response = await apiClient.get('/companies')
      return {
        success: true,
        data: response.data.data || response.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Get companies error:', error)
      return {
        success: false,
        message: 'Error al obtener empresas'
      }
    }
  },

  // Crear empresa
  async createCompany(companyData) {
    try {
      const response = await apiClient.post('/companies', companyData)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Create company error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Error al crear empresa',
        errors: error.response?.data?.errors || {}
      }
    }
  }
}

// Servicios para dashboard
export const dashboardService = {
  async getStats(params = {}) {
    try {
      const response = await apiClient.get('/dashboard/stats', { params })
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('Get dashboard stats error:', error)
      return {
        success: false,
        message: 'Error al obtener estadísticas'
      }
    }
  }
}

// Exportar cliente de API para uso directo si es necesario
export default apiClient

// También exportar métodos comunes para uso directo
export const api = {
  get: (url, config) => apiClient.get(url, config),
  post: (url, data, config) => apiClient.post(url, data, config),
  put: (url, data, config) => apiClient.put(url, data, config),
  delete: (url, config) => apiClient.delete(url, config),
  patch: (url, data, config) => apiClient.patch(url, data, config)
}